import { useEffect, useState } from "react";
import { formatRate } from "../calc";

interface PercentInputProps {
  /** 요율 (예: 0.0282 = 2.82%) */
  rate: number;
  onChange: (rate: number) => void;
  /** 원본과 달라져 수정됨(저장 전) 상태 */
  dirty?: boolean;
  /** 표준(기준) 요율 — 옆에 "기준 X%" 표시 */
  baseline?: number;
}

/**
 * 구성비 칸의 편집 가능한 요율 badge (예: [2.82%]).
 * 퍼센트 단위로 입력받아 소수 비율로 변환해 상위로 전달합니다.
 */
export function PercentInput({
  rate,
  onChange,
  dirty,
  baseline,
}: PercentInputProps) {
  const fmt = (r: number) => String(parseFloat((r * 100).toFixed(4)));
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => fmt(rate));

  // 편집 중엔 재포맷하지 않음 (한 글자씩 수정 가능). 포커스 밖일 때만 rate와 동기화
  useEffect(() => {
    if (!focused) setText(fmt(rate));
  }, [rate, focused]);

  const offStandard = baseline != null && Math.abs(baseline - rate) > 1e-9;

  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-flex items-center gap-0.5 rounded-md bg-primary/10 px-1.5 py-0.5 ring-1 ring-inset ring-transparent transition-colors hover:bg-primary/20 focus-within:bg-white focus-within:ring-primary">
        <input
          type="text"
          inputMode="decimal"
          value={text}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^0-9.]/g, "");
            setText(raw);
            const v = parseFloat(raw);
            if (!Number.isNaN(v)) onChange(v / 100);
          }}
          className="w-12 cursor-pointer bg-transparent text-right text-xs font-semibold text-primary outline-none focus:cursor-text"
          aria-label="요율(%)"
        />
        <span className="text-xs font-semibold text-primary">%</span>
        {dirty && (
          <span
            className="ml-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400"
            title="수정됨(저장 전)"
          />
        )}
      </span>
      {baseline != null && (
        <span
          className={`whitespace-nowrap text-2xs ${offStandard ? "text-amber-600" : "text-slate-400"}`}
          title="표준(기준) 요율"
        >
          기준 {formatRate(baseline)}
        </span>
      )}
    </span>
  );
}
